/**
 * Left column: what to analyse and how. Subject folder → pipeline → steps →
 * the selected step's parameters → Run. Everything here drives the same
 * pipeline session the classic workspace uses.
 */

import * as React from "react";
import { CheckCircle2, Circle, FileJson, FlaskConical, FolderOpen, Layers, Loader2, MinusCircle, Play, Scan, Square, XCircle } from "lucide-react";

import { Button } from "@/components/ui/primitives";
import { loadExampleSubject, pickDirectory, splitError } from "@/ipc/api";
import type { Modality, PipelineKind, StageState, SubjectScan } from "@/ipc/contract";
import { cn } from "@/lib/utils";
import { ParamTable } from "@/pipeline/ParamTable";
import { stageParamCount, stageParams } from "@/pipeline/params";
import { usePipelineSession } from "@/pipeline/session";
import { modalitiesFor, stagesFor } from "@/pipeline/stages";
import { useAppStore, useJob } from "@/store/appStore";
import { useSimple } from "./store";

const KINDS: { kind: PipelineKind; label: string; hint: string }[] = [
  { kind: "dwi", label: "DWI", hint: "Stroke core, ASPECTS, territories, report" },
  { kind: "pwi", label: "PWI", hint: "TTP, hypoperfusion and its volume" },
  { kind: "combined", label: "DWI + PWI", hint: "Both, with core against hypoperfusion" },
];

function kindFromScan(scan: SubjectScan): PipelineKind {
  if (scan.readiness.combined.ready) return "combined";
  if (scan.readiness.pwi.ready) return "pwi";
  return "dwi";
}

/** `keepKind`: the pipeline the reader already picked, kept when the folder supports it. */
async function openFolder(path: string, keepKind?: PipelineKind) {
  const session = usePipelineSession.getState();
  session.startNewAnalysis("dwi", "full");
  await session.chooseSubjectDir(path);
  const scan = usePipelineSession.getState().scan;
  if (scan) {
    session.setPipelineKind(keepKind && scan.readiness[keepKind].ready ? keepKind : kindFromScan(scan));
    useSimple.getState().setInputs(path, scan.files);
  }
  await session.createCaseFromDraft();
}

function Section({ title, children, className }: { title: string; children: React.ReactNode; className?: string }) {
  return (
    <section className={cn("border-b border-ads-border px-4 py-3", className)}>
      <h2 className="mb-2 text-[10.5px] font-semibold uppercase tracking-[0.12em] text-ads-faint">{title}</h2>
      {children}
    </section>
  );
}

/** The opened folder's files: click an image to read it, a mask to lay it over the image shown. */
function InputFiles() {
  const inputs = useSimple((s) => s.inputs);
  const baseId = useSimple((s) => s.baseId);
  const overlayIds = useSimple((s) => s.overlayIds);
  const previewId = useSimple((s) => s.previewId);
  return (
    <div className="mt-1.5 overflow-hidden rounded-md border border-ads-border">
      {inputs.map((f) => {
        const state = f.id === baseId ? "BASE" : overlayIds.includes(f.id) ? "OVERLAY" : f.id === previewId ? "OPEN" : null;
        return (
          <button
            key={f.id}
            type="button"
            onClick={() => useSimple.getState().show(f)}
            title={f.isMask ? "Lay over the current image" : f.isVolume ? "Show in the viewer" : "Preview"}
            className={cn(
              "flex w-full items-center gap-1.5 border-b border-ads-border px-2 py-1 text-left text-[11.5px] last:border-0",
              state ? "bg-ads-accent-soft text-ads-accent" : "hover:bg-ads-hover",
            )}
          >
            {f.isMask ? (
              <Layers className="h-3.5 w-3.5 shrink-0 text-amber-500" />
            ) : f.isVolume ? (
              <Scan className="h-3.5 w-3.5 shrink-0 text-ads-accent" />
            ) : (
              <FileJson className="h-3.5 w-3.5 shrink-0 text-ads-muted" />
            )}
            <span className="min-w-0 flex-1 truncate">{f.name}</span>
            {state === "BASE" ? <span className="rounded bg-ads-accent px-1 text-[9px] font-semibold text-white">BASE</span> : null}
            {state === "OVERLAY" ? <span className="rounded bg-amber-500 px-1 text-[9px] font-semibold text-white">OVERLAY</span> : null}
          </button>
        );
      })}
    </div>
  );
}

function StateIcon({ state }: { state: StageState | undefined }) {
  if (state === "running") return <Loader2 className="h-3.5 w-3.5 animate-spin text-ads-accent" />;
  if (state === "completed") return <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />;
  if (state === "failed") return <XCircle className="h-3.5 w-3.5 text-red-600" />;
  if (state === "skipped") return <MinusCircle className="h-3.5 w-3.5 text-amber-500" />;
  return <Circle className="h-3.5 w-3.5 text-ads-border-strong" />;
}

export function PipelinePanel() {
  const draft = usePipelineSession((s) => s.draft);
  const scan = usePipelineSession((s) => s.scan);
  const notice = usePipelineSession((s) => s.notice);
  const busy = usePipelineSession((s) => s.busy);
  const job = useJob();
  const running = job?.state === "running" || job?.state === "queued";
  const [selected, setSelected] = React.useState<{ modality: Modality; id: string } | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const pick = async (fn: () => Promise<{ path: string | null }>, keepKind?: PipelineKind) => {
    setError(null);
    try {
      const { path } = await fn();
      if (path) await openFolder(path, keepKind);
    } catch (e) {
      setError(splitError(e).message);
    }
  };

  const hasSubject = Boolean(draft.subjectDir && scan);
  const selectedStages = [...draft.stages.dwi, ...draft.stages.pwi].length;

  return (
    <div className="flex h-full min-h-0 flex-col bg-ads-surface">
      <div className="min-h-0 flex-1 overflow-y-auto">
        <Section title="Subject">
          <div className="flex gap-2">
            <Button size="sm" variant="primary" disabled={running} title={running ? "Stop the current run first" : undefined} onClick={() => void pick(() => pickDirectory("Open a subject folder"))}>
              <FolderOpen className="h-3.5 w-3.5" />
              Open folder…
            </Button>
            <Button size="sm" variant="outline" disabled={running} title={running ? "Stop the current run first" : undefined} onClick={() => {
                // The example follows the pipeline picked below: DWI-only, or the DWI+PWI subject.
                const kind = draft.pipelineKind;
                void pick(() => loadExampleSubject(kind === "dwi" ? "dwi" : "pwi"), kind);
              }}>
              <FlaskConical className="h-3.5 w-3.5" />
              Example
            </Button>
          </div>
          {hasSubject ? (
            <div className="mt-3 text-[12px]">
              <div className="font-medium text-ads-accent">{draft.subjectId}</div>
              <InputFiles />
            </div>
          ) : (
            <p className="mt-2 text-[11.5px] text-ads-muted">A folder with one subject's NIfTI files (DWI, ADC or b0; PWI with its JSON).</p>
          )}
          {error ? <p className="mt-2 text-[11.5px] text-red-600">{error}</p> : null}
        </Section>

        <Section title="Pipeline">
          <div className="grid grid-cols-3 gap-1 rounded-md bg-ads-bg p-0.5">
            {KINDS.map(({ kind, label, hint }) => {
              const ready = scan ? scan.readiness[kind].ready : true;
              const active = draft.pipelineKind === kind;
              return (
                <button
                  key={kind}
                  type="button"
                  disabled={running || (hasSubject && !ready)}
                  title={hasSubject && !ready ? `Missing: ${scan!.readiness[kind].missing.join(", ")}` : hint}
                  onClick={() => {
                    usePipelineSession.getState().setPipelineKind(kind);
                    setSelected(null);
                  }}
                  className={cn(
                    "rounded px-1 py-1.5 text-[11.5px] font-medium transition disabled:cursor-not-allowed disabled:opacity-40",
                    active ? "bg-ads-surface text-ads-accent shadow-sm" : "text-ads-muted hover:text-ads-text",
                  )}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </Section>

        <Section title="Steps">
          {modalitiesFor(draft.pipelineKind).map((modality) => {
            const key = modality === "DWI" ? "dwi" : "pwi";
            return (
              <div key={modality} className="mb-2 last:mb-0">
                {draft.pipelineKind === "combined" ? (
                  <div className="mb-1 text-[10.5px] font-semibold text-ads-faint">{modality}</div>
                ) : null}
                {stagesFor(modality).map((stage) => {
                  const checked = (draft.stages[key] as string[]).includes(stage.id);
                  const state = job?.stages.find((s) => s.modality === modality && s.id === stage.id)?.state;
                  const isSel = selected?.modality === modality && selected.id === stage.id;
                  return (
                    <div
                      key={stage.id}
                      className={cn(
                        "flex items-center gap-2 rounded px-1.5 py-1 text-[12.5px]",
                        isSel ? "bg-ads-accent-soft" : "hover:bg-ads-hover",
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={running}
                        onChange={() => usePipelineSession.getState().toggleStage(modality, stage.id)}
                        className="h-3.5 w-3.5 accent-[var(--color-ads-accent)]"
                      />
                      <button
                        type="button"
                        className="min-w-0 flex-1 truncate text-left"
                        onClick={() => setSelected(isSel ? null : { modality, id: stage.id })}
                        title={stage.description}
                      >
                        {stage.label}
                      </button>
                      <span className="text-[10.5px] text-ads-faint">{stageParamCount(modality, stage.id)}</span>
                      <StateIcon state={job ? state : undefined} />
                    </div>
                  );
                })}
              </div>
            );
          })}
          <p className="mt-1 text-[10.5px] text-ads-faint">Click a step to see and change its parameters.</p>
        </Section>

        {selected ? (
          <Section title={`Parameters · ${stagesFor(selected.modality).find((s) => s.id === selected.id)?.label ?? ""}`}>
            <ParamTable
              groups={stageParams(selected.modality, selected.id as never)}
              subjectId={draft.subjectId || "sub-XXXX"}
              openFirst={1}
            />
          </Section>
        ) : null}
      </div>

      <div className="shrink-0 border-t border-ads-border p-3">
        {job ? (
          <div className="mb-2">
            <div className="mb-1 flex justify-between text-[11px] text-ads-muted">
              <span className="truncate">
                {job.state === "running" ? job.currentStage ?? "Starting" : job.state === "completed" ? "Finished" : job.state}
              </span>
              <span>{job.progressPct}%</span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-ads-bg">
              <div
                className={cn("h-full transition-all", job.state === "failed" ? "bg-red-500" : "bg-ads-accent")}
                style={{ width: `${job.progressPct}%` }}
              />
            </div>
            {job.state === "failed" && job.errorMessage ? (
              <p className="mt-1 text-[11px] text-red-600">{job.errorMessage}</p>
            ) : null}
          </div>
        ) : null}
        {notice?.tone === "error" ? <p className="mb-2 text-[11px] text-red-600">{notice.message}</p> : null}
        {running ? (
          <Button size="lg" variant="danger" className="w-full" onClick={() => void usePipelineSession.getState().stop()}>
            <Square className="h-3.5 w-3.5" />
            Stop
          </Button>
        ) : (
          <Button
            size="lg"
            variant="primary"
            className="w-full"
            disabled={!hasSubject || selectedStages === 0 || busy}
            onClick={() => {
              useAppStore.getState().clearLog();
              void usePipelineSession.getState().run();
            }}
          >
            <Play className="h-3.5 w-3.5" />
            Run {selectedStages} step{selectedStages === 1 ? "" : "s"}
          </Button>
        )}
      </div>
    </div>
  );
}
